import { Icon } from '@buggy/shared'
import { Button, Flex, Spin, Table, Typography, type GetProp } from 'antd'
import { isNull, toNumber } from 'lodash-es'
import { FunctionComponent, useState, type ReactNode } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import styled, { useTheme } from 'styled-components'
import DeleteButton from '@/components/common/buttons/delete-button'
import PermissionInsufficientTooltip from '@/components/common/permission-insufficient-tooltip'
import CollaborationRecordListExpandedRow from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/collaboration-record-list-expanded-row'
import LegacyScoringList from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/legacy-scoring-list'
import useTableLocale from '@/hooks/antd/use-table-locale'
import { useAuth } from '@/hooks/use-auth'
import useFormatPrice from '@/hooks/use-format-price'
import useLocalizedKolQuotationCurrency from '@/hooks/use-localized-kol-quotation-currency'
import { useDirectoryActionPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  collaborationRecordSchema,
  CollaborationRecordScoringType,
  type CollaborationRecord,
  type CustomPostCollaborationRecord,
  type LegacyCollaborationRecord,
} from '@/types/schema/directory/collaboration-record'
import renderDashIfNil from '@/utils/render-dash-if-nil'

type DataType =
  | CustomPostCollaborationRecord
  | CollaborationRecord
  | LegacyCollaborationRecord
type ColumnProps = GetProp<
  typeof Table<CollaborationRecord | CustomPostCollaborationRecord>,
  'columns'
>[number]

export type CollaborationRecordListProps = {
  kolId: string
  kolName: string
  hasNextPage?: boolean
  isLoadMore?: boolean
  onLoadMore?: VoidFunction
} & (
  | {
      data?: (CollaborationRecord | LegacyCollaborationRecord)[]
      collaborationRecordScoringType: CollaborationRecordScoringType.Customized
      onDeleteRecord: (record: CollaborationRecord) => Promise<void>
      onClickEditRecord: (record: CollaborationRecord) => void
    }
  | {
      data?: (CustomPostCollaborationRecord | LegacyCollaborationRecord)[]
      collaborationRecordScoringType: CollaborationRecordScoringType.ReportKol
      onDeleteRecord?: never
      onClickEditRecord?: never
    }
  | {
      data?: (CollaborationRecord | LegacyCollaborationRecord)[]
      collaborationRecordScoringType: CollaborationRecordScoringType.CampaignKol
      onDeleteRecord?: never
      onClickEditRecord?: never
    }
)

const CollaborationRecordList: FunctionComponent<
  CollaborationRecordListProps
> = ({
  kolId,
  kolName,
  collaborationRecordScoringType,
  data,
  hasNextPage = false,
  isLoadMore = false,
  onLoadMore = (): void => {},
  onDeleteRecord,
  onClickEditRecord,
}) => {
  const { formatMessage, formatNumber, formatDateTimeRange } = useIntl()
  const { formatPrice } = useFormatPrice()
  const tableLocale = useTableLocale()
  const theme = useTheme()

  const localizedKolQuotationCurrency = useLocalizedKolQuotationCurrency()
  const { userInfo } = useAuth()
  const directoryActionPermission = useDirectoryActionPermission()

  const [expandedRowKeys, setExpandedRowKeys] = useState<DataType['id'][]>([])
  const [hoveredRowKey, setHoveredRowKey] = useState<number | null>(null)

  const [sentryRef] = useInfiniteScroll({
    loading: isLoadMore,
    hasNextPage,
    onLoadMore,
    disabled: !hasNextPage,
  })

  const legacyCollaborationRecordsScoringList =
    data
      ?.filter((record) => !record.is_new_evaluation)
      .flatMap((record) => record.scoring_list) ?? []
  const collaborationRecords =
    data?.filter((record) => record.is_new_evaluation) ?? []

  const getActionElements = (
    record: CollaborationRecord | CustomPostCollaborationRecord,
    isCreator: boolean,
  ): ReactNode[] => {
    if (
      collaborationRecordScoringType !==
      CollaborationRecordScoringType.Customized
    ) {
      return []
    }

    const elements: ReactNode[] = []

    if (onClickEditRecord && isCreator) {
      elements.push(
        <PermissionInsufficientTooltip
          insufficient={!directoryActionPermission}
          key={`edit-record-${record.id}`}
        >
          <Button
            disabled={!directoryActionPermission}
            icon={
              <Icon
                color={theme.colors.brand.primary}
                fontSize={16}
                name='edit-3'
              />
            }
            size='small'
            type='text'
            onClick={(event): void => {
              event.stopPropagation()
              onClickEditRecord(record as CollaborationRecord)
            }}
          />
        </PermissionInsufficientTooltip>,
      )
    }

    if (onDeleteRecord) {
      elements.push(
        <PermissionInsufficientTooltip
          insufficient={!directoryActionPermission}
          key={`delete-record-${record.id}`}
        >
          <DeleteButton
            confirmModalContent={formatMessage({
              id: 'irm:delete_quotation_modal_content',
            })}
            confirmModalTitle={formatMessage({
              id: 'kol:edit_coop_field_delete_price',
            })}
            disabled={!directoryActionPermission}
            iconColor={theme.colors.brand.primary}
            iconSize={16}
            size='small'
            type='text'
            onDelete={async (): Promise<void> =>
              await onDeleteRecord(record as CollaborationRecord)
            }
          />
        </PermissionInsufficientTooltip>,
      )
    }

    return elements
  }

  const columns: ColumnProps[] = [
    {
      title:
        collaborationRecordScoringType ===
        CollaborationRecordScoringType.ReportKol
          ? formatMessage({
              id: 'competitive_analyses:rename_modal_name_field',
            })
          : formatMessage({
              id: 'general:column_title_campaign_name',
            }),
      dataIndex: collaborationRecordSchema.keyof().enum.name,
      render: (_, collaborationRecord): ReactNode => {
        return (
          <Flex align='center' gap={12} style={{ paddingLeft: 8 }}>
            <ExpandButton
              icon={
                <Icon
                  fontSize={14}
                  name={
                    expandedRowKeys.includes(collaborationRecord.id)
                      ? 'chevron-down'
                      : 'chevron-right'
                  }
                />
              }
              type='text'
            />
            <Flex vertical gap={4}>
              <RecordName
                ellipsis={{ tooltip: collaborationRecord.name, rows: 2 }}
              >
                {collaborationRecord.name}
              </RecordName>
              <RecordDuration>
                {formatDateTimeRange(
                  collaborationRecord.start_at,
                  collaborationRecord.end_at,
                )}
              </RecordDuration>
            </Flex>
          </Flex>
        )
      },
    },
    {
      title: formatMessage({
        id: 'general:column_title_collaboration_compensation',
      }),
      width: 100,
      dataIndex: collaborationRecordSchema.keyof().enum.budget,
      render: (value: CollaborationRecord['budget']) => formatPrice(value),
    },
    {
      title: formatMessage({
        id: 'general:column_title_engagement_rate',
      }),
      width:
        collaborationRecordScoringType ===
        CollaborationRecordScoringType.Customized
          ? 200
          : 100,
      dataIndex: collaborationRecordSchema.keyof().enum.post_engagement_rate,
      render: (_, record): ReactNode => {
        const isCreator = record.manager_id === toNumber(userInfo?.id)
        const actionElements = getActionElements(record, isCreator)

        return (
          <Flex align='center' justify='space-between'>
            {renderDashIfNil(
              isNull(record.post_engagement_rate)
                ? null
                : formatNumber(record.post_engagement_rate, {
                    isPercentageDisplay: true,
                  }),
            )}
            {actionElements.length > 0 && hoveredRowKey === record.id && (
              <Flex>{actionElements}</Flex>
            )}
          </Flex>
        )
      },
    },
  ]

  return (
    <Flex vertical>
      <CurrencyLabel>{localizedKolQuotationCurrency}</CurrencyLabel>
      <StyledTable
        columns={columns}
        dataSource={collaborationRecords}
        expandable={{
          expandedRowRender: (collaborationRecord: CollaborationRecord) => (
            <CollaborationRecordListExpandedRow
              collaborationRecordScoringType={collaborationRecordScoringType}
              kolId={kolId}
              kolName={kolName}
              record={collaborationRecord}
            />
          ),
          onExpandedRowsChange: (expandedRowKeys: number[]) => {
            setExpandedRowKeys(expandedRowKeys)
          },
          expandRowByClick: true,
          rowExpandable: (collaborationRecord) =>
            collaborationRecord.name !== null,
        }}
        footer={() => (
          <>
            {legacyCollaborationRecordsScoringList?.length > 0 && (
              <LegacyScoringList
                collaborationRecordScoringType={collaborationRecordScoringType}
                data={legacyCollaborationRecordsScoringList}
                kolId={kolId}
              />
            )}
            {hasNextPage && (
              <Flex align='center' justify='center' ref={sentryRef}>
                <Spin />
              </Flex>
            )}
          </>
        )}
        locale={tableLocale}
        pagination={false}
        rowKey={(collaborationRecord) => collaborationRecord.id}
        onRow={(record) => ({
          onMouseEnter: (): void => {
            setHoveredRowKey(record.id)
          },
          onMouseLeave: (): void => {
            setHoveredRowKey(null)
          },
        })}
      />
    </Flex>
  )
}

const ExpandButton = styled(Button)`
  &.ant-btn {
    --ant-control-height: 14px;
    border: none;
    line-height: 14px;
  }

  &.ant-btn.ant-btn-icon-only:not(.ant-btn-sm):not(.ant-btn-block) {
    padding: 0;
  }
`

const RecordName = styled(Typography.Paragraph)`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;

  &.ant-typography {
    margin: 0;
  }
`

const RecordDuration = styled.div`
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const CurrencyLabel = styled.div`
  text-align: right;
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`

const StyledTable = styled(
  Table<CollaborationRecord | CustomPostCollaborationRecord>,
)`
  & .ant-table {
    --ant-table-expand-icon-size: 0;
    --ant-table-footer-bg: transparent;
  }

  &.ant-table-wrapper {
    table {
      border-collapse: collapse;
    }

    .ant-table-tbody > tr > td {
      border: none;
    }

    .ant-table-footer {
      padding: 40px 0 0;
    }
  }

  .ant-table-content {
    .ant-table-expand-icon-col {
      width: 0;
      border: none;
      padding: 0;
    }
  }

  .ant-table-thead {
    .ant-table-row-expand-icon-cell {
      padding: 0;
    }

    .ant-table-cell {
      font-weight: 400;

      &:nth-child(2) {
        padding: 16px 8px;
      }
    }
  }

  &.ant-table-wrapper tr.ant-table-expanded-row > td {
    padding: 0;
    background: ${({ theme }): string => theme.colors.background.white};
  }

  .ant-table-tbody {
    .ant-table-row:has(
        + .ant-table-expanded-row:not([style*='display: none'])
      ) {
      border-bottom: none;
    }

    .ant-table-row,
    .ant-table-expanded-row {
      border-bottom: var(--ant-line-width) var(--ant-line-type)
        var(--ant-table-border-color);
    }

    .ant-table-row {
      .ant-table-row-expand-icon-cell {
        padding: 0;
      }

      .ant-table-expanded-row {
        .ant-table-cell {
          padding: 0;
        }
      }

      .ant-table-cell:nth-child(2) {
        padding: 16px 8px 16px 0;
      }
    }
  }
`

export default CollaborationRecordList
